import { useApplicationResourcesQuery } from "@graphql/application-resources/application-resources.g";
import { useClientEmployeeTotalCountQuery } from "@graphql/client-employees/clientEmployees.g";
import { useUnresolvedNotificationsTotalCountQuery } from "@graphql/notifications/Notifications.g";
import { useCallback, useMemo, useState } from "react";
import { Outlet, useNavigate } from "react-router";

import { CoveragePlaceholderCard } from "@components/Cards/CoveragePlaceholderCard/CoveragePlaceholderCard";
import { coverageDefinition } from "@components/Cards/CoveragePlaceholderCard/coverageDefinition";
import { RecentAlertsList } from "@components/Cards/RecentAlertsCard/RecentAlertsCard";
import { useRecentAlerts } from "@components/Cards/RecentAlertsCard/useRecentAlerts";
import { NewAppsDiscoveryTrendChart } from "@components/Charts";
import {
  DashboardCell,
  DashboardFigure,
  DashboardGrid,
  DashboardRangeControl,
  DashboardWidget,
  dashboardRangeEndingToday,
  type DashboardDateRange,
} from "@components/Dashboards/shell";
import { TotalTenantsStat } from "@components/Stats";
import { ApplicationsUnreviewedTable } from "@components/Tables";
import { ALL_NOTIFICATION_TYPES } from "@utils/notificationTypes";

import { Clickable } from "@design-system/Clickable/Clickable";
import { Text } from "@design-system/Text/Text";

import { routes } from "@pages/routes";

export const MSPDashboard = () => {
  const navigate = useNavigate();
  const openTenants = () => {
    navigate(routes.portal.clients.$path(), { viewTransition: true });
  };

  const [discoveryRange, setDiscoveryRange] = useState<DashboardDateRange>(
    () => dashboardRangeEndingToday(90),
  );
  const [discoverySummary, setDiscoverySummary] = useState({
    totalCount: 0,
    isLoading: true,
  });
  const handleDiscoverySummary = useCallback(
    (summary: { totalCount: number; isLoading: boolean }) => {
      setDiscoverySummary(summary);
    },
    [],
  );

  const totalClientCountQuery = useClientEmployeeTotalCountQuery();
  const employeeCount =
    totalClientCountQuery.data?.clientEmployees?.totalCount || 0;

  const applicationResourcesQuery = useApplicationResourcesQuery({
    first: 1,
    offset: 0,
    isPrimaryResource: true,
    isSaas: true,
  });
  const resourceGroupCount =
    applicationResourcesQuery.data?.applicationResources?.totalCount || 0;

  const unresolvedNotificationCountQuery =
    useUnresolvedNotificationsTotalCountQuery({
      notificationType_In: ALL_NOTIFICATION_TYPES,
    });
  const notificationCount =
    unresolvedNotificationCountQuery.data?.notifications?.totalCount || 0;

  const alerts = useRecentAlerts();

  const discoveryDefinition = useMemo(() => {
    if (discoverySummary.isLoading) {
      return undefined;
    }
    const count = discoverySummary.totalCount;
    return `${count} new application${count === 1 ? "" : "s"}`;
  }, [discoverySummary]);

  return (
    <>
      <DashboardGrid>
        <DashboardCell span={3}>
          <DashboardFigure
            prominent
            label="Users for all tenants"
            value={employeeCount.toString()}
            isLoading={totalClientCountQuery.isLoading}
            onClick={openTenants}
          />
        </DashboardCell>
        <DashboardCell span={3}>
          <DashboardFigure
            prominent
            label="Discovered SaaS"
            value={resourceGroupCount.toString()}
            isLoading={applicationResourcesQuery.isLoading}
            onClick={openTenants}
          />
        </DashboardCell>
        <DashboardCell span={3}>
          <DashboardFigure
            prominent
            label="Unresolved notifications"
            value={notificationCount.toString()}
            isLoading={unresolvedNotificationCountQuery.isLoading}
          />
        </DashboardCell>
        <DashboardCell span={3}>
          <TotalTenantsStat prominent onClick={openTenants} />
        </DashboardCell>

        <DashboardCell span={12}>
          <DashboardWidget title="Applications still to review">
            <ApplicationsUnreviewedTable search="" />
          </DashboardWidget>
        </DashboardCell>

        <DashboardCell span={8}>
          <DashboardWidget
            title="Recent alerts"
            definition={
              alerts.isLoading
                ? undefined
                : `${alerts.totalCount} unresolved`
            }
            actions={
              alerts.totalCount > 0 ? (
                <Clickable onClick={alerts.handleSeeAll}>
                  <Text size={12} color="primary" weight={600}>
                    See all
                  </Text>
                </Clickable>
              ) : null
            }
          >
            <RecentAlertsList />
          </DashboardWidget>
        </DashboardCell>

        <DashboardCell span={4}>
          <DashboardWidget
            title="Coverage problems"
            definition={coverageDefinition("msp")}
          >
            <CoveragePlaceholderCard variant="msp" />
          </DashboardWidget>
        </DashboardCell>

        <DashboardCell span={12}>
          <DashboardWidget
            title="New applications discovered"
            definition={discoveryDefinition}
            actions={
              <DashboardRangeControl
                value={discoveryRange}
                onChange={setDiscoveryRange}
              />
            }
          >
            <NewAppsDiscoveryTrendChart
              dateRange={discoveryRange}
              onSummaryChange={handleDiscoverySummary}
            />
          </DashboardWidget>
        </DashboardCell>
      </DashboardGrid>
      <Outlet />
    </>
  );
};
